/**
 * t566u: how long an unseen bell notification waits before it is emailed. Each person picks one in
 * Settings > Notifications (null = follow the organisation default); admins set the default in Workspace >
 * Notification emails. 0 = never email unseen notifications. 10 minutes is the long-standing behaviour.
 */
export const EMAIL_DELAY_NEVER = 0;
export const EMAIL_DELAY_STANDARD = 10;
/** Minutes. 10 is the standard (and starting organisation default). */
export const EMAIL_DELAY_CHOICES = [5, 10, 15, 30, 60, 240, 1440, EMAIL_DELAY_NEVER] as const;
export type EmailDelayMinutes = (typeof EMAIL_DELAY_CHOICES)[number];

export const isEmailDelayChoice = (v: unknown): v is EmailDelayMinutes => typeof v === 'number' && (EMAIL_DELAY_CHOICES as readonly number[]).includes(v);

/** "5 minutes", "1 hour", "24 hours", "Never". */
export function emailDelayLabel(min: number): string {
  if (min === EMAIL_DELAY_NEVER) return 'Never';
  if (min % 60 === 0) { const h = min / 60; return `${h} hour${h === 1 ? '' : 's'}`; }
  return `${min} minute${min === 1 ? '' : 's'}`;
}

/** Options for a picker. Pass the organisation default to add "Organisation default (…)" first (value null). */
export function emailDelayOptions(orgDefault?: number): { value: number | null; label: string }[] {
  const opts: { value: number | null; label: string }[] = EMAIL_DELAY_CHOICES.map(m => ({ value: m as number, label: m === EMAIL_DELAY_NEVER ? 'Never (no emails for unseen notifications)' : `After ${emailDelayLabel(m)}` }));
  if (orgDefault === undefined) return opts;
  const d = orgDefault === EMAIL_DELAY_NEVER ? 'never' : `after ${emailDelayLabel(orgDefault)}`;
  return [{ value: null, label: `Organisation default (${d})` }, ...opts];
}

/** Personal choice, else the organisation default, else the standard 10 minutes. */
export const effectiveEmailDelay = (personal: number | null | undefined, orgDefault: number | null | undefined): number =>
  isEmailDelayChoice(personal) ? personal : isEmailDelayChoice(orgDefault) ? orgDefault : EMAIL_DELAY_STANDARD;
